import React, { useEffect } from 'react';
import type { ShippingAddressFragment } from '@graphql/aliases';
import { useAddressDialogController } from './AddressDialog.controller';
import { StyledAddressDialogContent, styles } from './AddressDialog.styles';
import { DialogV2, Box, Flex } from '@withjoy/joykit';
import { SlideInTransition } from '@shared/components/SlideInTransition';
import { AddressForm } from './components/AddressForm';
import { AddressConfirmation } from './components/AddressConfirmation';
import { AddressDialogProvider } from './AddressDialog.provider';
import { useShippingAddressTelemetry } from '../ShippingAddressAndCurrencyDialogsProvider/ShippingAddress.telemetry';

export interface AddressDialogProps
  extends Readonly<{
    eventId: string;
    size?: 'xl' | '2xl';
    registryShippingAddress: ShippingAddressFragment | null;
    isOpen: boolean;
    onClose: () => void;
    onSave?: (shippingAddress: ShippingAddressFragment) => void;
    addressDialogTrigger: string;
  }> {}

export const AddressDialog: React.FC<AddressDialogProps> = ({ isOpen, size = '2xl', onClose, eventId, registryShippingAddress, addressDialogTrigger, onSave }) => {
  const { saveAddress, mutationLoading, step, setStep, handleClose } = useAddressDialogController({
    eventId,
    onClose,
    onSave,
  });

  const { addressDialogOpened } = useShippingAddressTelemetry({ addToContext: { eventId } });
  useEffect(() => {
    if (isOpen) {
      addressDialogOpened(addressDialogTrigger);
    }
  }, [addressDialogOpened, addressDialogTrigger, isOpen]);

  const screens = [
    {
      key: 'AddressForm',
      isIn: step === 1,
      component: (
        <AddressForm
          eventId={eventId}
          registryShippingAddress={registryShippingAddress}
        />
      ),
    },
    {
      key: 'AddressConfirmation',
      isIn: step === 2,
      component: <AddressConfirmation />,
    },
  ];

  return (
    <AddressDialogProvider
      eventId={eventId}
      mutationLoading={mutationLoading}
      onClose={handleClose}
      saveAddress={saveAddress}
      step={step}
      setStep={setStep}
    >
      <DialogV2
        size={size}
        isOpen={isOpen}
        enableDividers
        overrides={{ Root: { props: { height: 'auto' } }, Content: { props: { overflowX: 'clip' } } }}
      >
        <Box height={['100vh', '820px', '820px']}>
          <StyledAddressDialogContent __css={styles.addressDialogContent}>
            {screens.map(screen => (
              <SlideInTransition
                key={screen.key}
                isIn={screen.isIn}
                from="right"
              >
                <Flex>{screen.component}</Flex>
              </SlideInTransition>
            ))}
          </StyledAddressDialogContent>
        </Box>
      </DialogV2>
    </AddressDialogProvider>
  );
};
